import { AxiosError } from "axios";
import apiClient, { ApiError, isApiError } from "./api";

// Define interfaces matching the backend Pydantic models (or import if possible)
// Keep this in sync with backend/schemas.py

// Export the enum and detailed interfaces
export enum PositionEnum {
  ACADEMIA = "academia",
  DATA_SCIENCE = "data_science",
  ENGINEER = "engineer",
}

export interface ExperienceModel {
  company: string;
  title: string;
  description?: string | null;
  company_url?: string | null;
  start_date?: string | null; // Date as string (YYYY-MM-DD)
  end_date?: string | null; // Date as string (YYYY-MM-DD)
}

export interface ProjectModel {
  name: string;
  description?: string | null;
  url?: string | null;
}

export interface ContestModel {
  name: string;
  organizer?: string | null;
  description?: string | null;
}

export interface MiscInfoModel {
  key: string;
  value: string;
}

// Added ScoreModel interface
export interface ScoreModel {
  id: number;
  user_id?: number | null; // Assuming user_id might not always be needed on frontend model
  name?: string | null;
  value?: number | null;
  verified?: boolean | null;
  created_at: string; // Dates are typically strings in JSON
}

// Export the main interface
export interface ResumeData {
  name?: string | null;
  email?: string | null;
  school_name?: string | null;
  class_year?: number | null;
  position?: PositionEnum | null;
  experience: ExperienceModel[];
  projects: ProjectModel[];
  contests: ContestModel[];
  misc: MiscInfoModel[];
  scores: ScoreModel[]; // Added scores field
}

/**
 * Uploads and parses a PDF file using the backend's Gemini integration
 * @param file - The PDF file to upload and analyze
 * @returns A promise resolving to the parsed structured data (ResumeData)
 */
export const parsePdf = async (file: File): Promise<ResumeData> => {
  console.log("[parse.ts] parsePdf called with file:", file.name);

  try {
    const formData = new FormData();
    formData.append("pdf", file);

    // Expecting a JSON response matching the ResumeData interface
    const response = await apiClient.post<ResumeData>("/api/parse", formData, {
      headers: {
        // Let browser set Content-Type for FormData
        "Content-Type": undefined,
      },
    });
    console.log("[parse.ts] parsePdf successful:", response.data);
    return response.data; // Return the structured JSON object
  } catch (error: unknown) {
    console.error("[parse.ts] parsePdf failed:", error);
    if (error instanceof AxiosError) {
      const axiosError = error as AxiosError<ApiError>;
      if (axiosError.response) {
        console.error("Error parsing PDF:", axiosError.response.data);
        // Throw the structured error from backend if available
        throw axiosError.response.data;
      } else {
        console.error("Axios error (no response):", axiosError.message);
        throw new Error(`Network error parsing PDF: ${axiosError.message}`);
      }
    } else if (error instanceof Error) {
      console.error("An unexpected error occurred:", error.message);
      throw new Error(
        `An unexpected error occurred while parsing PDF: ${error.message}`
      );
    } else {
      console.error("An unexpected non-error was thrown:", error);
      throw new Error("An unexpected non-error occurred while parsing PDF.");
    }
  }
};
